"use client";

import { useEffect, useRef } from "react";
import { CheckCircle2, CircleDashed } from "lucide-react";
import type { Locale } from "@/lib/i18n";

const TICK_COUNT = 220;

export interface Milestone {
  date: string;
  title: string;
  accent: string;
  body: string;
  status?: "progress" | "planned" | "coming";
  progress?: number;
}

export const PAST_MILESTONES: Record<Locale, Milestone[]> = {
  sv: [
    {
      date: "Mars 2026",
      title: "Batteriet styrs",
      accent: "automatiskt",
      body: "Laddning och urladdning planeras efter batterinivå, väntad solproduktion, förbrukning och elpris. Planen räknas om var femtonde minut.",
    },
    {
      date: "Mars 2026",
      title: "Dynamisk",
      accent: "omplanering",
      body: "När produktion eller förbrukning avviker från prognosen ändras styrningen direkt. Batteriet följer inte en låst dagsplan.",
    },
    {
      date: "April 2026",
      title: "Pilotdrift i",
      accent: "fält",
      body: "Styrningen testas mot verkliga anläggningar. Mätdata från batteri, solproduktion och nät används för att justera beteendet innan bredare utrullning.",
    },
    {
      date: "April 2026",
      title: "Besparing och",
      accent: "översikt",
      body: "Appen visar besparing, solproduktion, förbrukning, batterinivå och utbyte med nätet. Besparingen delas upp så att det syns vad batteristyrningen bidrar med.",
    },
    {
      date: "Maj 2026",
      title: "Portal för",
      accent: "installatörer",
      body: "Installatörer lägger upp anläggningar, kopplar utrustning och hanterar kunder via en portal på webben.",
    },
    {
      date: "Maj 2026",
      title: "Snabb",
      accent: "registrering",
      body: "En guidad process tar installatören från adress på kartan till färdig anläggning på några minuter: enheter kopplas, tariffer sätts och första kunden bjuds in i samma flöde.",
    },
    {
      date: "Maj 2026",
      title: "Elräkningar",
      accent: "skannas in",
      body: "Installatören kan skanna in kundens elfaktura. Tariffer, nätavgifter och fasta kostnader läses ut och används i besparingsberäkningen.",
    },
    {
      date: "Q3 2026",
      title: "Elbilsladdning i",
      accent: "styrningen",
      body: "Laddningen anpassas efter husets förbrukning, solproduktion, batterinivå och elpris.",
    },
  ],
  en: [
    {
      date: "March 2026",
      title: "Battery controlled",
      accent: "automatically",
      body: "Charging and discharging are planned around battery level, expected solar production, consumption and electricity prices. The plan is recalculated every fifteen minutes.",
    },
    {
      date: "March 2026",
      title: "Dynamic",
      accent: "replanning",
      body: "When production or consumption deviates from the forecast, the control adjusts immediately. The battery does not follow a locked daily plan.",
    },
    {
      date: "April 2026",
      title: "Pilot operation in the",
      accent: "field",
      body: "The control is tested against real installations. Measurement data from battery, solar production and grid is used to tune the behavior before a wider rollout.",
    },
    {
      date: "April 2026",
      title: "Savings and",
      accent: "overview",
      body: "The app shows savings, solar production, consumption, battery level and grid exchange. Savings are broken down so you can see what the battery control contributes.",
    },
    {
      date: "May 2026",
      title: "Portal for",
      accent: "installers",
      body: "Installers set up plants, connect equipment and manage customers through a web portal.",
    },
    {
      date: "May 2026",
      title: "Fast",
      accent: "onboarding",
      body: "A guided flow takes the installer from an address on the map to a fully configured plant in minutes: devices are connected, tariffs are set and the first customer is invited in the same flow.",
    },
    {
      date: "May 2026",
      title: "Electricity bills",
      accent: "scanned in",
      body: "The installer can scan the customer's electricity bill. Tariffs, grid fees and fixed costs are extracted and used in the savings calculation.",
    },
    {
      date: "Q3 2026",
      title: "EV charging in the",
      accent: "control loop",
      body: "Charging adapts to the home's consumption, solar production, battery level and electricity prices.",
    },
  ],
  no: [
    {
      date: "Mars 2026",
      title: "Batteriet styres",
      accent: "automatisk",
      body: "Lading og utlading planlegges etter batterinivå, forventet solproduksjon, forbruk og strømpris. Planen beregnes på nytt hvert femtende minutt.",
    },
    {
      date: "Mars 2026",
      title: "Dynamisk",
      accent: "omplanlegging",
      body: "Når produksjon eller forbruk avviker fra prognosen, endres styringen umiddelbart. Batteriet følger ikke en låst dagsplan.",
    },
    {
      date: "April 2026",
      title: "Pilotdrift i",
      accent: "felt",
      body: "Styringen testes mot virkelige anlegg. Måledata fra batteri, solproduksjon og nett brukes til å justere atferden før en bredere utrulling.",
    },
    {
      date: "April 2026",
      title: "Besparelse og",
      accent: "oversikt",
      body: "Appen viser besparelse, solproduksjon, forbruk, batterinivå og utveksling med nettet. Besparelsen deles opp slik at du ser hva batteristyringen bidrar med.",
    },
    {
      date: "Mai 2026",
      title: "Portal for",
      accent: "installatører",
      body: "Installatører setter opp anlegg, kobler til utstyr og administrerer kunder via en nettportal.",
    },
    {
      date: "Mai 2026",
      title: "Rask",
      accent: "registrering",
      body: "En veiledet prosess tar installatøren fra en adresse på kartet til et ferdig konfigurert anlegg på noen minutter: enheter kobles til, tariffer settes og den første kunden inviteres i samme flyt.",
    },
    {
      date: "Mai 2026",
      title: "Strømregninger",
      accent: "skannes inn",
      body: "Installatøren kan skanne inn kundens strømregning. Tariffer, nettleie og faste kostnader leses ut og brukes i besparelsesberegningen.",
    },
    {
      date: "Q3 2026",
      title: "Elbillading i",
      accent: "styringen",
      body: "Ladingen tilpasses husets forbruk, solproduksjon, batterinivå og strømpris.",
    },
  ],
  da: [
    {
      date: "Marts 2026",
      title: "Batteriet styres",
      accent: "automatisk",
      body: "Opladning og afladning planlægges efter batteriniveau, forventet solproduktion, forbrug og elpris. Planen genberegnes hvert femtende minut.",
    },
    {
      date: "Marts 2026",
      title: "Dynamisk",
      accent: "omplanlægning",
      body: "Når produktion eller forbrug afviger fra prognosen, ændres styringen med det samme. Batteriet følger ikke en låst dagsplan.",
    },
    {
      date: "April 2026",
      title: "Pilotdrift i",
      accent: "marken",
      body: "Styringen testes mod virkelige anlæg. Måledata fra batteri, solproduktion og net bruges til at justere adfærden før en bredere udrulning.",
    },
    {
      date: "April 2026",
      title: "Besparelse og",
      accent: "overblik",
      body: "Appen viser besparelse, solproduktion, forbrug, batteriniveau og udveksling med nettet. Besparelsen opdeles, så du kan se, hvad batteristyringen bidrager med.",
    },
    {
      date: "Maj 2026",
      title: "Portal til",
      accent: "installatører",
      body: "Installatører opsætter anlæg, tilslutter udstyr og administrerer kunder via en webportal.",
    },
    {
      date: "Maj 2026",
      title: "Hurtig",
      accent: "onboarding",
      body: "Et guidet forløb fører installatøren fra en adresse på kortet til et fuldt konfigureret anlæg på få minutter: enheder tilsluttes, tariffer indstilles, og den første kunde inviteres i samme forløb.",
    },
    {
      date: "Maj 2026",
      title: "Elregninger",
      accent: "scannes ind",
      body: "Installatøren kan scanne kundens elregning. Tariffer, netgebyrer og faste omkostninger udlæses og bruges i besparelsesberegningen.",
    },
    {
      date: "Q3 2026",
      title: "Elbilopladning i",
      accent: "styringen",
      body: "Opladningen tilpasses husets forbrug, solproduktion, batteriniveau og elpris.",
    },
  ],
  fi: [
    {
      date: "Maaliskuu 2026",
      title: "Akkua ohjataan",
      accent: "automaattisesti",
      body: "Lataus ja purku suunnitellaan akun varaustason, odotetun aurinkotuotannon, kulutuksen ja sähkön hinnan mukaan. Suunnitelma lasketaan uudelleen viidentoista minuutin välein.",
    },
    {
      date: "Maaliskuu 2026",
      title: "Dynaaminen",
      accent: "uudelleensuunnittelu",
      body: "Kun tuotanto tai kulutus poikkeaa ennusteesta, ohjaus mukautuu välittömästi. Akku ei noudata lukittua päiväsuunnitelmaa.",
    },
    {
      date: "Huhtikuu 2026",
      title: "Pilottikäyttö",
      accent: "kentällä",
      body: "Ohjausta testataan todellisissa kohteissa. Akun, aurinkotuotannon ja verkon mittausdataa käytetään toiminnan hienosäätöön ennen laajempaa käyttöönottoa.",
    },
    {
      date: "Huhtikuu 2026",
      title: "Säästöt ja",
      accent: "yleiskatsaus",
      body: "Sovellus näyttää säästöt, aurinkotuotannon, kulutuksen, akun varaustason ja verkon kanssa vaihdetun energian. Säästöt eritellään, jotta näet, mitä akun ohjaus tuo.",
    },
    {
      date: "Toukokuu 2026",
      title: "Portaali",
      accent: "asentajille",
      body: "Asentajat perustavat laitoksia, kytkevät laitteita ja hallitsevat asiakkaita verkkoportaalin kautta.",
    },
    {
      date: "Toukokuu 2026",
      title: "Nopea",
      accent: "käyttöönotto",
      body: "Ohjattu vaiheistus vie asentajan kartan osoitteesta valmiiksi määritettyyn laitokseen muutamassa minuutissa: laitteet kytketään, tariffit asetetaan ja ensimmäinen asiakas kutsutaan samassa kulussa.",
    },
    {
      date: "Toukokuu 2026",
      title: "Sähkölaskut",
      accent: "skannataan",
      body: "Asentaja voi skannata asiakkaan sähkölaskun. Tariffit, verkkomaksut ja kiinteät kustannukset luetaan ja käytetään säästölaskennassa.",
    },
    {
      date: "Q3 2026",
      title: "Sähköauton lataus",
      accent: "ohjaukseen",
      body: "Lataus mukautuu talon kulutukseen, aurinkotuotantoon, akun varaustasoon ja sähkön hintaan.",
    },
  ],
  de: [
    {
      date: "März 2026",
      title: "Batterie wird",
      accent: "automatisch gesteuert",
      body: "Laden und Entladen werden anhand von Batteriestand, erwarteter Solarproduktion, Verbrauch und Strompreis geplant. Der Plan wird alle fünfzehn Minuten neu berechnet.",
    },
    {
      date: "März 2026",
      title: "Dynamische",
      accent: "Neuplanung",
      body: "Wenn Produktion oder Verbrauch von der Prognose abweichen, passt sich die Steuerung sofort an. Die Batterie folgt keinem festen Tagesplan.",
    },
    {
      date: "April 2026",
      title: "Pilotbetrieb im",
      accent: "Feld",
      body: "Die Steuerung wird an realen Anlagen getestet. Messdaten von Batterie, Solarproduktion und Netz werden genutzt, um das Verhalten vor einem breiteren Rollout zu optimieren.",
    },
    {
      date: "April 2026",
      title: "Einsparungen und",
      accent: "Übersicht",
      body: "Die App zeigt Einsparungen, Solarproduktion, Verbrauch, Batteriestand und den Austausch mit dem Netz. Die Einsparungen werden aufgeschlüsselt, damit du siehst, was die Batteriesteuerung beiträgt.",
    },
    {
      date: "Mai 2026",
      title: "Portal für",
      accent: "Installateure",
      body: "Installateure richten Anlagen ein, verbinden Geräte und verwalten Kunden über ein Webportal.",
    },
    {
      date: "Mai 2026",
      title: "Schnelles",
      accent: "Onboarding",
      body: "Ein geführter Ablauf bringt den Installateur in wenigen Minuten von einer Adresse auf der Karte zu einer vollständig konfigurierten Anlage: Geräte werden verbunden, Tarife eingestellt und der erste Kunde im selben Ablauf eingeladen.",
    },
    {
      date: "Mai 2026",
      title: "Stromrechnungen",
      accent: "eingescannt",
      body: "Der Installateur kann die Stromrechnung des Kunden einscannen. Tarife, Netzentgelte und Fixkosten werden ausgelesen und in der Einsparungsberechnung verwendet.",
    },
    {
      date: "Q3 2026",
      title: "E-Auto-Laden in der",
      accent: "Steuerung",
      body: "Das Laden passt sich an Verbrauch, Solarproduktion, Batteriestand und Strompreis des Hauses an.",
    },
  ],
  cs: [
    {
      date: "Březen 2026",
      title: "Baterie je řízena",
      accent: "automaticky",
      body: "Nabíjení a vybíjení se plánují podle stavu baterie, očekávané výroby ze slunce, spotřeby a ceny elektřiny. Plán se přepočítává každých patnáct minut.",
    },
    {
      date: "Březen 2026",
      title: "Dynamické",
      accent: "přeplánování",
      body: "Když se výroba nebo spotřeba odchýlí od předpovědi, řízení se okamžitě přizpůsobí. Baterie nesleduje pevný denní plán.",
    },
    {
      date: "Duben 2026",
      title: "Pilotní provoz v",
      accent: "terénu",
      body: "Řízení se testuje na reálných instalacích. Naměřená data z baterie, solární výroby a sítě se používají k doladění chování před širším nasazením.",
    },
    {
      date: "Duben 2026",
      title: "Úspory a",
      accent: "přehled",
      body: "Aplikace zobrazuje úspory, solární výrobu, spotřebu, stav baterie a výměnu se sítí. Úspory jsou rozděleny tak, abyste viděli, čím přispívá řízení baterie.",
    },
    {
      date: "Květen 2026",
      title: "Portál pro",
      accent: "instalatéry",
      body: "Instalatéři zakládají instalace, připojují zařízení a spravují zákazníky prostřednictvím webového portálu.",
    },
    {
      date: "Květen 2026",
      title: "Rychlé",
      accent: "zprovoznění",
      body: "Průvodce provede instalatéra od adresy na mapě k plně nakonfigurované instalaci během několika minut: zařízení se připojí, nastaví se tarify a v témže postupu se pozve první zákazník.",
    },
    {
      date: "Květen 2026",
      title: "Účty za elektřinu",
      accent: "se naskenují",
      body: "Instalatér může naskenovat zákazníkův účet za elektřinu. Tarify, síťové poplatky a fixní náklady se načtou a použijí ve výpočtu úspor.",
    },
    {
      date: "Q3 2026",
      title: "Nabíjení elektromobilu v",
      accent: "řízení",
      body: "Nabíjení se přizpůsobuje spotřebě domu, solární výrobě, stavu baterie a ceně elektřiny.",
    },
  ],
  zh: [
    {
      date: "2026年3月",
      title: "电池",
      accent: "自动控制",
      body: "充电和放电根据电池电量、预期光伏发电、用电量和电价进行规划。计划每十五分钟重新计算一次。",
    },
    {
      date: "2026年3月",
      title: "动态",
      accent: "重新规划",
      body: "当发电或用电偏离预测时，控制会立即调整。电池不会遵循固定的每日计划。",
    },
    {
      date: "2026年4月",
      title: "现场",
      accent: "试运行",
      body: "控制在真实安装现场进行测试。来自电池、光伏发电和电网的测量数据用于在更大范围推广前调整运行方式。",
    },
    {
      date: "2026年4月",
      title: "节省与",
      accent: "总览",
      body: "应用显示节省、光伏发电、用电量、电池电量以及与电网的交换。节省会被拆分，让你看到电池控制的贡献。",
    },
    {
      date: "2026年5月",
      title: "安装商",
      accent: "门户",
      body: "安装商通过网页门户创建电站、连接设备并管理客户。",
    },
    {
      date: "2026年5月",
      title: "快速",
      accent: "接入",
      body: "引导式流程让安装商在几分钟内从地图上的地址完成电站的全部配置：连接设备、设置电价，并在同一流程中邀请第一位客户。",
    },
    {
      date: "2026年5月",
      title: "电费账单",
      accent: "扫描录入",
      body: "安装商可以扫描客户的电费账单。电价、电网费用和固定成本被提取并用于节省计算。",
    },
    {
      date: "2026年第三季度",
      title: "电动车充电纳入",
      accent: "控制",
      body: "充电会根据家庭用电量、光伏发电、电池电量和电价进行调整。",
    },
  ],
};

export const FUTURE_MILESTONES: Record<Locale, Milestone[]> = {
  sv: [
    {
      date: "Q3 2026",
      title: "Värmepump i",
      accent: "styrningen",
      body: "Integrationen och avläsningen är på plats. Styrningen testas och finjusteras i pilot innan bredare lansering.",
      status: "progress",
      progress: 80,
    },
    {
      date: "Pilot · Steg 1",
      title: "Shelly-enheter",
      accent: "läses av",
      body: "Enheter och sensorer kopplas in i en pilot. Intelligence läser status, effekt och energiförbrukning i realtid.",
      status: "progress",
    },
    {
      date: "Pilot · Steg 2",
      title: "Shelly-enheter",
      accent: "styrs",
      body: "I piloten slår Intelligence av och på utvalda enheter utifrån elpris, solproduktion och husets behov.",
      status: "progress",
    },
    {
      date: "Kommer senare",
      title: "Elavtal i",
      accent: "Intelligence",
      body: "Elavtal kopplas till hemmets faktiska förbrukning och styrning, så att kostnad och villkor kan vägas in i optimeringen.",
      status: "coming",
    },
  ],
  en: [
    {
      date: "Q3 2026",
      title: "Heat pump in the",
      accent: "control loop",
      body: "The integration and data reading are in place. Control is being tested and refined in a pilot before a wider launch.",
      status: "progress",
      progress: 80,
    },
    {
      date: "Pilot · Stage 1",
      title: "Shelly devices are",
      accent: "read",
      body: "Devices and sensors are connected in a pilot. Intelligence reads status, power and energy use in real time.",
      status: "progress",
    },
    {
      date: "Pilot · Stage 2",
      title: "Shelly devices are",
      accent: "controlled",
      body: "In the pilot, Intelligence switches selected devices on and off based on electricity prices, solar production and household needs.",
      status: "progress",
    },
    {
      date: "Coming later",
      title: "Electricity contracts in",
      accent: "Intelligence",
      body: "Electricity contracts connect with actual household use and control, allowing costs and terms to inform the optimization.",
      status: "coming",
    },
  ],
  no: [
    {
      date: "Q3 2026",
      title: "Varmepumpe i",
      accent: "styringen",
      body: "Integrasjonen og avlesningen er på plass. Styringen testes og finjusteres i pilot før en bredere lansering.",
      status: "progress",
      progress: 80,
    },
    {
      date: "Pilot · Trinn 1",
      title: "Shelly-enheter",
      accent: "leses av",
      body: "Enheter og sensorer kobles til i en pilot. Intelligence leser status, effekt og energiforbruk i sanntid.",
      status: "progress",
    },
    {
      date: "Pilot · Trinn 2",
      title: "Shelly-enheter",
      accent: "styres",
      body: "Neste trinn lar Intelligence slå utvalgte enheter av og på basert på strømpris, solproduksjon og boligens behov.",
      status: "progress",
    },
    {
      date: "Kommer senere",
      title: "Strømavtaler i",
      accent: "Intelligence",
      body: "Strømavtaler kobles til boligens faktiske forbruk og styring, slik at kostnader og vilkår kan tas med i optimaliseringen.",
      status: "coming",
    },
  ],
  da: [
    {
      date: "Q3 2026",
      title: "Varmepumpe i",
      accent: "styringen",
      body: "Integrationen og dataaflæsningen er på plads. Styringen testes og finjusteres i en pilot før en bredere lancering.",
      status: "progress",
      progress: 80,
    },
    {
      date: "Pilot · Trin 1",
      title: "Shelly-enheder",
      accent: "aflæses",
      body: "Enheder og sensorer tilsluttes i en pilot. Intelligence aflæser status, effekt og energiforbrug i realtid.",
      status: "progress",
    },
    {
      date: "Pilot · Trin 2",
      title: "Shelly-enheder",
      accent: "styres",
      body: "Næste trin lader Intelligence tænde og slukke udvalgte enheder ud fra elpris, solproduktion og boligens behov.",
      status: "progress",
    },
    {
      date: "Kommer senere",
      title: "Elaftaler i",
      accent: "Intelligence",
      body: "Elaftaler kobles til boligens faktiske forbrug og styring, så omkostninger og vilkår kan indgå i optimeringen.",
      status: "coming",
    },
  ],
  fi: [
    {
      date: "Q3 2026",
      title: "Lämpöpumppu",
      accent: "ohjaukseen",
      body: "Integraatio ja tietojen luku ovat valmiina. Ohjausta testataan ja hienosäädetään pilotissa ennen laajempaa julkaisua.",
      status: "progress",
      progress: 80,
    },
    {
      date: "Pilotti · Vaihe 1",
      title: "Shelly-laitteiden tietoja",
      accent: "luetaan",
      body: "Laitteet ja anturit liitetään pilottiin. Intelligence lukee tilan, tehon ja energiankulutuksen reaaliajassa.",
      status: "progress",
    },
    {
      date: "Pilotti · Vaihe 2",
      title: "Shelly-laitteita",
      accent: "ohjataan",
      body: "Seuraavassa vaiheessa Intelligence kytkee valittuja laitteita päälle ja pois sähkön hinnan, aurinkotuotannon ja kodin tarpeiden mukaan.",
      status: "progress",
    },
    {
      date: "Tulossa myöhemmin",
      title: "Sähkösopimukset",
      accent: "Intelligencessä",
      body: "Sähkösopimukset yhdistetään kodin todelliseen kulutukseen ja ohjaukseen, jotta kustannukset ja ehdot voidaan huomioida optimoinnissa.",
      status: "coming",
    },
  ],
  de: [
    {
      date: "Q3 2026",
      title: "Wärmepumpe in der",
      accent: "Steuerung",
      body: "Integration und Datenauslesung sind vorhanden. Die Steuerung wird in einem Pilotprojekt getestet und vor dem breiteren Start verfeinert.",
      status: "progress",
      progress: 80,
    },
    {
      date: "Pilot · Stufe 1",
      title: "Shelly-Geräte werden",
      accent: "ausgelesen",
      body: "Geräte und Sensoren werden in einem Pilotprojekt verbunden. Intelligence liest Status, Leistung und Energieverbrauch in Echtzeit.",
      status: "progress",
    },
    {
      date: "Pilot · Stufe 2",
      title: "Shelly-Geräte werden",
      accent: "gesteuert",
      body: "In der nächsten Stufe schaltet Intelligence ausgewählte Geräte anhand von Strompreis, Solarproduktion und Bedarf des Hauses ein und aus.",
      status: "progress",
    },
    {
      date: "Kommt später",
      title: "Stromverträge in",
      accent: "Intelligence",
      body: "Stromverträge werden mit dem tatsächlichen Verbrauch und der Steuerung verknüpft, damit Kosten und Konditionen in die Optimierung einfließen.",
      status: "coming",
    },
  ],
  cs: [
    {
      date: "Q3 2026",
      title: "Tepelné čerpadlo v",
      accent: "řízení",
      body: "Integrace a načítání dat jsou hotové. Řízení se testuje a dolaďuje v pilotním provozu před širším spuštěním.",
      status: "progress",
      progress: 80,
    },
    {
      date: "Pilot · Fáze 1",
      title: "Zařízení Shelly se",
      accent: "načítají",
      body: "Zařízení a senzory se připojí v pilotním provozu. Intelligence načítá stav, výkon a spotřebu energie v reálném čase.",
      status: "progress",
    },
    {
      date: "Pilot · Fáze 2",
      title: "Zařízení Shelly se",
      accent: "řídí",
      body: "V další fázi Intelligence zapíná a vypíná vybraná zařízení podle ceny elektřiny, solární výroby a potřeb domácnosti.",
      status: "progress",
    },
    {
      date: "Přijde později",
      title: "Smlouvy na elektřinu v",
      accent: "Intelligence",
      body: "Smlouvy na elektřinu se propojí se skutečnou spotřebou a řízením domácnosti, aby optimalizace zohlednila náklady a podmínky.",
      status: "coming",
    },
  ],
  zh: [
    {
      date: "2026年第三季度",
      title: "热泵纳入",
      accent: "控制",
      body: "集成和数据读取已就绪。控制功能正在试点中测试和优化，之后将扩大推出。",
      status: "progress",
      progress: 80,
    },
    {
      date: "试点 · 第一阶段",
      title: "读取",
      accent: "Shelly 设备",
      body: "设备和传感器接入试点。Intelligence 实时读取状态、功率和能源消耗。",
      status: "progress",
    },
    {
      date: "试点 · 第二阶段",
      title: "控制",
      accent: "Shelly 设备",
      body: "下一阶段，Intelligence 会根据电价、光伏发电和家庭需求开关选定设备。",
      status: "progress",
    },
    {
      date: "稍后推出",
      title: "Intelligence 中的",
      accent: "电力合同",
      body: "电力合同将与家庭实际用电和控制相结合，让优化过程能够考虑成本和合同条款。",
      status: "coming",
    },
  ],
};

export const LABELS: Record<
  Locale,
  { timeline: string; today: string; planned: string; coming: string }
> = {
  sv: { timeline: "Tidslinje", today: "Idag", planned: "Planerat", coming: "Kommer senare" },
  en: { timeline: "Timeline", today: "Today", planned: "Planned", coming: "Coming later" },
  no: { timeline: "Tidslinje", today: "I dag", planned: "Planlagt", coming: "Kommer senere" },
  da: { timeline: "Tidslinje", today: "I dag", planned: "Planlagt", coming: "Kommer senere" },
  fi: { timeline: "Aikajana", today: "Tänään", planned: "Suunniteltu", coming: "Tulossa myöhemmin" },
  de: { timeline: "Zeitachse", today: "Heute", planned: "Geplant", coming: "Kommt später" },
  cs: { timeline: "Časová osa", today: "Dnes", planned: "Plánováno", coming: "Přijde později" },
  zh: { timeline: "时间线", today: "今天", planned: "计划中", coming: "稍后推出" },
};

function MilestoneRow({
  item,
  future = false,
  side = "right",
  anchorRef,
  plannedLabel = "Planerat",
  comingLabel = "Kommer senare",
}: {
  item: Milestone;
  future?: boolean;
  /** Which side the card sits on in the centered (laptop) layout. */
  side?: "left" | "right";
  anchorRef?: (el: HTMLSpanElement | null) => void;
  plannedLabel?: string;
  comingLabel?: string;
}) {
  const leftCard = side === "left";
  const inProgress = item.status === "progress";
  const planned = item.status === "planned";
  const coming = item.status === "coming";
  // Base (<lg) and 2xl keep the left-rail layout; lg..xl center the rail and
  // push the card to one half.
  const cardLayout = leftCard
    ? "lg:flex-none lg:w-1/2 lg:pl-0 lg:pr-16 lg:text-right"
    : "lg:flex-none lg:w-1/2 lg:ml-auto lg:pl-16 lg:pr-0 lg:text-left";

  return (
    <li className="relative flex pb-12 last:pb-0">
      {/* Invisible anchor — its position tells the ruler which tick to elongate.
          data-side tells the ruler which way the marker line should point. */}
      <span ref={anchorRef} data-side={side} aria-hidden className="absolute left-0 top-2 h-px w-px" />

      {/* Content cell */}
      <div className={`min-w-0 flex-1 -mt-1 pl-16 sm:pl-32 ${cardLayout}`}>
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.12em] ${
            inProgress
              ? "text-amber-700"
              : coming
                ? "text-soft-slate/45"
                : future
                  ? "text-soft-slate/70"
                  : "text-pine"
          }`}
        >
          {item.date}
          {!future && <CheckCircle2 aria-hidden className="h-3.5 w-3.5 text-[#16a34a]" strokeWidth={2.5} />}
          {inProgress && <CircleDashed aria-hidden className="h-3.5 w-3.5" strokeWidth={2.5} />}
        </span>
        <h3
          className={`mt-1 font-display text-xl sm:text-2xl font-normal leading-tight tracking-tight ${
            coming ? "text-pine/35 blur-[0.4px]" : planned ? "text-pine/55" : "text-pine"
          }`}
        >
          {item.title} {item.accent}
        </h3>
        <p
          className={`mt-2 max-w-[46ch] text-sm sm:text-base leading-relaxed ${
            leftCard ? "lg:ml-auto" : ""
          } ${coming ? "text-soft-slate/40 blur-[0.55px]" : planned ? "text-soft-slate/80" : "text-soft-slate"}`}
        >
          {item.body}
        </p>
        {inProgress && item.progress !== undefined && (
          <div
            className={`mt-4 max-w-[18rem] ${leftCard ? "lg:ml-auto" : ""}`}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={item.progress}
          >
            <div className="h-1.5 overflow-hidden rounded-full bg-amber-950/10">
              <span
                aria-hidden
                className="block h-full rounded-full bg-amber-500"
                style={{ width: `${item.progress}%` }}
              />
            </div>
          </div>
        )}
        {planned && (
          <span className="mt-3 inline-block rounded-full border border-soft-slate/30 px-3 py-0.5 text-[0.7rem] font-medium uppercase tracking-[0.1em] text-soft-slate">
            {plannedLabel}
          </span>
        )}
        {coming && (
          <span className="mt-3 inline-block rounded-full border border-dashed border-soft-slate/20 px-3 py-0.5 text-[0.7rem] font-medium uppercase tracking-[0.1em] text-soft-slate/60">
            {comingLabel}
          </span>
        )}
      </div>
    </li>
  );
}

// All locales carry the same number of milestones, so the marker math is
// locale-independent.
const PAST_COUNT = PAST_MILESTONES.sv.length;
const TODAY_INDEX = PAST_COUNT;
const MARKER_COUNT = PAST_MILESTONES.sv.length + 1 + FUTURE_MILESTONES.sv.length;

type MarkerKind = "past" | "today" | "future";
function markerKind(i: number): MarkerKind {
  if (i < PAST_COUNT) return "past";
  if (i === TODAY_INDEX) return "today";
  return "future";
}

export function Timeline({ locale = "sv" }: { locale?: Locale }) {
  const pastMilestones = PAST_MILESTONES[locale];
  const futureMilestones = FUTURE_MILESTONES[locale];
  const labels = LABELS[locale];
  const railRef = useRef<HTMLDivElement | null>(null);
  const tickRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const markerRefs = useRef<(HTMLSpanElement | null)[]>([]);
  // tick index -> the milestone sitting on it (kind + exact offset within the rail)
  const milestoneTicksRef = useRef<Map<number, { kind: MarkerKind; top: number }>>(new Map());
  // How far the content text sits from the rail (the milestone line stops just before it).
  const insetRef = useRef(128);
  const spacingRef = useRef(10);
  // Layout mode + the rail's horizontal centre, shared between layout() and update().
  const centeredRef = useRef(false);
  const centerPxRef = useRef(0);

  useEffect(() => {
    let raf = 0;

    // The centered (rail-in-the-middle, cards left & right) layout is used only
    // for the laptop range. Mobile (<lg) and 2xl keep the left-rail layout.
    const isCentered = () => window.matchMedia("(min-width: 1024px)").matches;

    // Position ticks at integer pixel offsets so every gap is identical.
    const layout = () => {
      const rail = railRef.current;
      if (!rail) return;
      const railRect = rail.getBoundingClientRect();
      const railTop = railRect.top;
      const height = railRect.height;
      const centered = isCentered();
      const centerPx = railRect.width / 2;
      centeredRef.current = centered;
      centerPxRef.current = centerPx;

      const spacing = Math.max(2, Math.round(height / (TICK_COUNT - 1)));
      spacingRef.current = spacing;
      for (let i = 0; i < TICK_COUNT; i++) {
        const tick = tickRefs.current[i];
        if (!tick) continue;
        tick.style.top = `${i * spacing}px`;
      }

      // Map each milestone to a ruler tick, align that tick exactly to the
      // milestone's row, and draw a static line pointing toward its card.
      const map = new Map<number, { kind: MarkerKind; top: number }>();
      for (let m = 0; m < MARKER_COUNT; m++) {
        const anchor = markerRefs.current[m];
        if (!anchor) continue;
        const offset = anchor.getBoundingClientRect().top - railTop;
        const idx = Math.max(0, Math.min(TICK_COUNT - 1, Math.round(offset / spacing)));
        map.set(idx, { kind: markerKind(m), top: offset });
        const tick = tickRefs.current[idx];
        if (!tick) continue;
        tick.style.top = `${offset}px`;

        // Which way the line points (centered layout) and how far the card sits.
        const side = anchor.dataset.side === "left" ? "left" : "right";
        const content = anchor.nextElementSibling as HTMLElement | null;
        let inset = insetRef.current;
        if (content) {
          const cs = getComputedStyle(content);
          const measured = centered ? parseFloat(side === "left" ? cs.paddingRight : cs.paddingLeft) : parseFloat(cs.paddingLeft);
          if (!Number.isNaN(measured)) {
            inset = measured;
            insetRef.current = measured;
          }
        }
        const markerWidth = Math.max(0, inset - 20);
        tick.style.width = `${markerWidth}px`;
        tick.style.opacity = "1";
        if (centered && side === "left") {
          tick.style.left = `${centerPx - markerWidth}px`;
          tick.style.background = "linear-gradient(to left, rgba(10,10,10,0.5), rgba(10,10,10,0))";
        } else {
          tick.style.left = centered ? `${centerPx}px` : "0px";
          tick.style.background = "linear-gradient(to right, rgba(10,10,10,0.5), rgba(10,10,10,0))";
        }
      }
      milestoneTicksRef.current = map;
    };

    const update = () => {
      raf = 0;
      const rail = railRef.current;
      if (!rail) return;
      const rect = rail.getBoundingClientRect();
      const focal = window.innerHeight / 2;
      const sigma = 16;
      const spacing = spacingRef.current;
      const milestones = milestoneTicksRef.current;
      const centered = centeredRef.current;
      const centerPx = centerPxRef.current;

      for (let i = 0; i < TICK_COUNT; i++) {
        const tick = tickRefs.current[i];
        if (!tick) continue;

        // Milestone lines are styled statically in layout(); leave them be.
        if (milestones.has(i)) continue;

        const y = rect.top + i * spacing;
        const dist = y - focal;
        const wave = Math.exp(-(dist * dist) / (2 * sigma * sigma));
        const len = 5 + 20 * wave;
        const opacity = 0.16 + 0.5 * wave;
        tick.style.background = "";
        tick.style.width = `${len.toFixed(1)}px`;
        tick.style.opacity = opacity.toFixed(2);
        // Centered: grow symmetrically from the middle; otherwise grow from the left.
        tick.style.left = centered ? `${(centerPx - len / 2).toFixed(1)}px` : "0px";
      }
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    const onResize = () => {
      layout();
      update();
    };

    layout();
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="px-3 md:px-4 py-16 md:py-28">
      <div className="mx-auto max-w-[1600px]">
        {/* Small centered label above the ruler */}
        <div className="text-center">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-pine">{labels.timeline}</span>
        </div>

        {/* Timeline body */}
        <div className="relative mt-10 md:mt-12">
          {/* Ruler overlay (animated wave ticks only) */}
          <div
            ref={railRef}
            className="pointer-events-none absolute -top-14 -bottom-14 left-0 right-0 [mask-image:linear-gradient(to_bottom,transparent,black_3.5rem,black_calc(100%_-_3.5rem),transparent)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,black_3.5rem,black_calc(100%_-_3.5rem),transparent)]"
            aria-hidden
          >
            {/* Animated ruler ticks */}
            {Array.from({ length: TICK_COUNT }).map((_, i) => (
              <span
                key={i}
                ref={(el) => {
                  tickRefs.current[i] = el;
                }}
                className="absolute h-[2px] -translate-y-1/2 rounded-full bg-pine"
                style={{
                  top: `${i * 10}px`,
                  left: 0,
                  width: "5px",
                  opacity: 0.16,
                }}
              />
            ))}
          </div>

          {/* Past milestones */}
          <ol className="relative">
            {pastMilestones.map((item, i) => (
              <MilestoneRow
                key={item.title}
                item={item}
                side={i % 2 === 0 ? "left" : "right"}
                anchorRef={(el) => {
                  markerRefs.current[i] = el;
                }}
              />
            ))}
          </ol>

          {/* TODAY marker — no ruler tick, just the label + line */}
          <div className="relative mt-8 pb-16 md:mt-12 md:pb-20">
            <div className="pl-16 sm:pl-32 lg:pl-0 lg:text-center">
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-red-500">{labels.today}</span>
              {/* Red line — pointing right normally, symmetric in the centered layout */}
              <div className="mt-2 h-px w-full bg-gradient-to-r from-red-500 via-red-500/60 to-transparent lg:from-transparent lg:via-red-500" />
            </div>
          </div>

          {/* Future milestones */}
          <ol className="relative">
            {futureMilestones.map((item, i) => (
              <MilestoneRow
                key={`${item.date}-${item.title}`}
                item={item}
                future
                plannedLabel={labels.planned}
                comingLabel={labels.coming}
                side={i % 2 === 0 ? "left" : "right"}
                anchorRef={(el) => {
                  markerRefs.current[TODAY_INDEX + 1 + i] = el;
                }}
              />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
